import { useState, useEffect } from 'react';
import { Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from './AuthContext.js';
import { KordaroLogo, KordaroMark } from '../../core/shared/components/KordaroLogo.js';

interface InviteInfo {
  email: string;
  entityName?: string;
  createdByName?: string;
}

export function RegisterPage() {
  const { register } = useAuth();
  const [token, setToken] = useState('');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [language, setLanguage] = useState('nl');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setTokenError('Geen uitnodigingstoken gevonden. Controleer de link in je e-mail.');
      setValidating(false);
      return;
    }
    setToken(t);

    fetch(`/api/auth/invite/validate?token=${encodeURIComponent(t)}`)
      .then(res => {
        if (!res.ok) throw new Error('invalid');
        return res.json();
      })
      .then(data => {
        setInviteInfo({ email: data.email, entityName: data.entityName, createdByName: data.createdByName });
      })
      .catch(() => {
        setTokenError('Deze uitnodiging is verlopen of al gebruikt. Vraag een nieuwe aan bij je beheerder.');
      })
      .finally(() => setValidating(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Wachtwoord moet minstens 8 tekens bevatten');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }
    if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Wachtwoord moet minstens 1 hoofdletter en 1 cijfer bevatten');
      return;
    }

    setLoading(true);
    try {
      await register({
        email: inviteInfo!.email,
        password,
        firstName,
        lastName,
        inviteToken: token,
      });
      setSuccess(true);
      setTimeout(() => { window.location.href = '/'; }, 1500);
    } catch (err: any) {
      setError(err.message || 'Registratie mislukt');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gold mx-auto mb-4" />
          <p className="text-gray-500">Uitnodiging controleren...</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="flex justify-center mb-2"><KordaroLogo width={200} /></h1>
          </div>
          <div className="bg-card border border-card-border rounded-[14px] p-8 shadow-sm text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Uitnodiging ongeldig</h2>
            <p className="text-gray-500 text-sm mb-6">{tokenError}</p>
            <a href="/" className="inline-block w-full py-2.5 px-4 bg-gold hover:bg-gold-dark text-white font-medium rounded-lg transition-colors text-center">
              Naar inlogpagina
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Account aangemaakt!</h2>
          <p className="text-gray-500">Je wordt doorgestuurd naar het dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="flex justify-center mb-2"><KordaroLogo width={200} /></h1>
          <p className="text-gray-500 mt-1">Maak je account aan</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border border-card-border rounded-[14px] p-8 shadow-sm">
          {inviteInfo?.entityName && (
            <div className="mb-5 p-3 bg-gold/5 border border-gold/20 rounded-lg text-sm">
              <span className="text-gray-600">Je bent uitgenodigd voor </span>
              <span className="font-semibold text-gray-900">{inviteInfo.entityName}</span>
              {inviteInfo.createdByName && (
                <span className="text-gray-600"> door {inviteInfo.createdByName}</span>
              )}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mailadres</label>
            <input
              type="email"
              value={inviteInfo?.email || ''}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              disabled
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Voornaam</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Achternaam</label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
                required
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Wachtwoord</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
                placeholder="Min. 8 tekens, 1 hoofdletter, 1 cijfer"
                required
                minLength={8}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Wachtwoord bevestigen</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
              placeholder="Herhaal wachtwoord"
              required
              minLength={8}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Taal</label>
            <select
              value={language}
              onChange={e => setLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold"
            >
              <option value="nl">Nederlands</option>
              <option value="fr">Frans</option>
              <option value="en">Engels</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-gold hover:bg-gold-dark text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Account aanmaken...
              </>
            ) : (
              'Account aanmaken'
            )}
          </button>

          <p className="text-center text-sm text-gray-500 mt-4">
            Al een account?{' '}
            <a href="/" className="text-gold font-medium hover:underline">Inloggen</a>
          </p>
        </form>
      </div>
    </div>
  );
}
